// The first-run guidance overlay (onboarding design D-Guide), ported from the
// promoted sketch `prototype/sketches/onboarding/first-run-guide.html`. A
// GEOMETRY-INDEPENDENT full-screen layer: `position: fixed; inset: 0`, a scrim +
// centred paper card composed from token CSS vars — it never measures the
// dock/sheet/map, so it survives the open Colony `Sheet` content-box clip (D-087)
// and its content/step-advance/dismiss are assertable in jsdom without real layout
// (D-086). Being a fixed layer above the sheet scrim, it renders over everything the
// Colony screen draws.
//
// Presentational only (the codebase convention: screens wire the store, components
// stay pure): the step index and the Next/Back/dismiss callbacks are owned by
// `Colony.tsx`, which holds the step in the store (`guideStep`) so it survives a
// route-exit unmount and can be cleared by the non-persisting `resetGuideProgress`.
//
// The 3-step sequence teaches one topic per step, in the Field-Journal voice: the
// watch -> diagnose -> prompt -> hot-swap core loop (DESIGN §1), the bee-type dock +
// script sheet, and the open map (scout-to-know). Copy references only UI that
// actually renders (no eggs counter — D-069).
//
// **Rounds mode gets its own copy, not the live copy** (WAG-11): the shipped steps
// were false there — there is no hot-swap (a rounds document is candidate data that
// takes effect at the next **Run**, DESIGN §2/§6), and the prompt is a globally
// pinned bar rather than living inside the bee-type sheet (DESIGN §9), so the
// geography Colony's step 2 points at does not exist on the Sandbox screen. Both
// tables share the third step verbatim (`MAP_STEP`) — "the map is open" is true in
// both modes — and `steps` is an optional prop (default `LIVE_GUIDE_STEPS`) so
// `Colony.tsx` needs no change at all: the smaller diff of the two options the task
// allowed (a `steps` prop vs. two components) is to keep the component the single
// source of both tables and let the live call site stay silent about which one it
// wants.

import type { CSSProperties, ReactNode } from 'react';
import { ASSET_MANIFEST } from '../../../canvas/assets';
import { spriteForType } from '../../rounds/type-visuals';
import { HUD_ICONS } from '../hud/hud-icons';

interface GuideStep {
  glyph: string;
  title: string;
  body: ReactNode;
}

/** Visually hidden, for glyphs that are pictures of controls: a bare ▶ or a sprite announces
 * nothing, so each depiction carries the control's real name for a screen reader. */
const SR_ONLY: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

/**
 * **The Run pill, drawn as the player sees it** (WAG-109).
 *
 * Deliberately the pill and NOT the `▶` box beside it. `run-submit-model.ts` gives the same play
 * glyph to `needs-running` (accent, "Run this version", which simulates the new plan) and to
 * `paused` (neutral, "Play", which replays a trace that already exists), so an arrow in the copy
 * would point at the wrong control half the time. The pill carries the word "Run" and appears only
 * where the activation is `submit`, which makes it the unambiguous half of that pair.
 *
 * It needs no visually-hidden label: the word is visible text, so it is already what a screen
 * reader reads. An extra one duplicated as "Run Run" on copy-paste.
 */
const RUN_PILL_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 20,
  // `baseline`, unlike the sprite chips' -2px, and the difference is structural rather than taste.
  // An inline-flex box takes its baseline from its first item: this pill's item is TEXT, so at
  // `baseline` the word "Run" sits on the same line as the sentence around it - which is what
  // aligned looks like. The chips hold an image, which has no baseline of its own, so they sit on
  // the baseline by their bottom edge and genuinely do need nudging down.
  verticalAlign: 'baseline',
  margin: '0 2px',
  boxSizing: 'border-box',
  padding: '0 8px',
  borderRadius: 'var(--r-pill)',
  border: '1px solid var(--accent-strong)',
  background: 'var(--accent-fill)',
  color: '#FFFDF6',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 11,
  lineHeight: 1,
};

function RunPill() {
  return <span style={RUN_PILL_STYLE}>Run</span>;
}

/** The Beekeeper, drawn as the 🐝 that actually leads the prompt field (`PromptBar.tsx`), so
 * "tell the Beekeeper" names something the player can point at. */
function BeekeeperMark() {
  return (
    <span aria-hidden style={{ fontSize: 13 }}>
      🐝{' '}
    </span>
  );
}

/**
 * A miniature of one bee-type chip from the dock — the real sprite at the real chip's colour, so
 * "tap a bee" shows what a bee looks like in this UI rather than describing where the dock is.
 * Harvester because it is the default birth type and therefore the chip a new colony has most of.
 */
const CHIP_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 20,
  // matches the Run pill's -2px for the same reason (see RUN_PILL_STYLE)
  verticalAlign: '-2px',
  margin: '0 2px',
  boxSizing: 'border-box',
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--line-strong)',
  background: 'var(--paper-1)',
};

const PIXEL_SPRITE: CSSProperties = {
  width: 13,
  height: 13,
  imageRendering: 'pixelated',
  objectFit: 'contain',
};

/**
 * The five shipped presets, in the order the dock lists them (DESIGN §3). All five have bees at
 * the start under the `rounds` preset (`warrior = 4`) - it is the `seed` preset, which live mode
 * plays, that starts with none. Worth stating because the two differ and the copy says "five".
 */
const PRESET_TYPES = ['harvester', 'nurse', 'builder', 'scout', 'warrior'] as const;

/** All five type chips in a row, for "you start with five bee types". */
function BeeChipRow() {
  return (
    <>
      {PRESET_TYPES.map((type) => (
        <BeeChip key={type} type={type} silent />
      ))}
      <span style={SR_ONLY}>{PRESET_TYPES.join(', ')}</span>
    </>
  );
}

/**
 * A miniature of one bee-type chip from the dock - the real sprite in the real chip frame, so the
 * copy shows what a bee type looks like in this UI rather than describing where the dock is.
 *
 * `silent` drops the screen-reader label for the case where the surrounding sentence already names
 * the type ("Tap a bee type ...", "So scouts ... find the flowers"), which would otherwise be read
 * twice - and shows up in a copy-paste as visible duplication.
 */
function BeeChip({ type = 'harvester', silent = false }: { type?: string; silent?: boolean }) {
  return (
    <>
      <span aria-hidden style={CHIP_STYLE}>
        <img src={spriteForType(type)} alt="" style={PIXEL_SPRITE} />
      </span>
      {silent ? null : <span style={SR_ONLY}>a bee type</span>}
    </>
  );
}

/**
 * A bare world sprite inline in the copy (nectar, a flower) - no chip frame, because these are
 * things in the world rather than controls the player can press, and framing them would imply a
 * button. Always `aria-hidden`: every use sits beside the word it illustrates.
 */
function InlineSprite({ src }: { src: string }) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      style={{ ...PIXEL_SPRITE, width: 15, height: 16, verticalAlign: '-3px', margin: '0 2px' }}
    />
  );
}

/** A step's bullets. Compact on purpose: this is read on a phone, over the world view, and every
 * line costs screen. */
const LIST_STYLE: CSSProperties = {
  margin: 0,
  paddingLeft: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

/** Step 3 is identical prose in both modes ("nothing is hidden, but your bees only
 * know what they've seen" — DESIGN §9's information model is mode-independent), so
 * it is written once and shared by both tables rather than duplicated. */
const MAP_STEP: GuideStep = {
  glyph: '🗺️',
  title: "Nothing is hidden, but your bees only know what they've seen.",
  body: (
    <ul style={LIST_STYLE}>
      <li>
        You can see the whole map. A bee only sees a short distance around itself, but they
        communicate between each other by dancing.
      </li>
      <li>
        A bee can act on what it has <b>sensed</b>, <b>heard in a dance</b>, or <b>remembered</b> -
        nothing else.
      </li>
      <li>
        So scouts <BeeChip type="scout" silent /> find the flowers, and dances share them with the
        hive.
      </li>
      <li>
        Talk to the <BeekeeperMark />
        <b>Beekeeper</b> to make sure your bees can navigate the map effectively.
      </li>
    </ul>
  ),
};

/**
 * The fourth field note (rounds only): what the player is actually optimising for.
 *
 * Rounds-specific on purpose - it says "the round", and live mode has no round. Live keeps its
 * three steps, which is why the two tables no longer share a length.
 */
const OPTIMISE_STEP: GuideStep = {
  glyph: '🍯',
  title: 'Optimise for many bees.',
  body: (
    <ul style={LIST_STYLE}>
      <li>The objective is to finish the round with as many bees as possible.</li>
      <li>
        {/* Root-absolute via the registry (merge fix, `pvp-ui-fixes` W1): this guide landed on
            main against the old bare-name convention, under which the icon 404s on any
            two-segment route (`/demo/red`, `/review/:round`). The icon-url-convention scan
            caught it at merge time — its first real catch. */}
        To produce bees you need nectar <InlineSprite src={HUD_ICONS.nectar} /> that is extracted
        from flowers <InlineSprite src={ASSET_MANIFEST.flowerPink} />.
      </li>
      <li>
        Too much nectar stored means you are not making enough bees. Too little and your hive is in
        danger - find the middle point.
      </li>
    </ul>
  ),
};

/** The live Colony screen's 3-topic sequence (design D-Guide / sketch), and this
 * component's default `steps`. */
export const LIVE_GUIDE_STEPS: readonly GuideStep[] = [
  {
    glyph: '🐝',
    title: "You don't steer your bees, you teach them.",
    body: (
      <ul style={LIST_STYLE}>
        <li>Watch the colony.</li>
        <li>Spot what's going wrong.</li>
        <li>
          Tell the <BeekeeperMark />
          <b>Beekeeper</b> what to change.
        </li>
        <li>
          The new plan <b>hot-swaps</b> into the live hive.
        </li>
        <li>Watch again. That loop is the game.</li>
      </ul>
    ),
  },
  {
    glyph: '🗂️',
    title: 'Each bee type has its own little program.',
    body: (
      <ul style={LIST_STYLE}>
        <li>
          You start with five bee types <BeeChipRow />.
        </li>
        <li>
          Tap a bee type <BeeChip silent /> to see its code (only if you&rsquo;re curious - you can
          skip the code entirely!).
        </li>
        <li>
          Just ask the <BeekeeperMark />
          <b>Beekeeper</b> below about the code of your bees - it will read the code and tell you
          what it does.
        </li>
      </ul>
    ),
  },
  MAP_STEP,
];

/** The Sandbox screen's 3-topic sequence — same arc, same titles where the claim still
 * holds, corrected where the live copy would be lying about rounds mode (see the file
 * header). Step 1 names **Run**, not hot-swap; step 2 points at the pinned prompt bar,
 * not a prompt box "below" the sheet (there is no such geography on this screen). */
export const ROUNDS_GUIDE_STEPS: readonly GuideStep[] = [
  {
    glyph: '🐝',
    title: "You don't steer your bees, you teach them.",
    body: (
      <ul style={LIST_STYLE}>
        <li>
          If you see a <RunPill /> button, press it - it starts the game.
        </li>
        <li>Watch your bees work, and spot what should be improved.</li>
        <li>
          Tell the <BeekeeperMark />
          <b>Beekeeper</b> what to change (e.g. &ldquo;nurse bees are staying still, why?&rdquo; or
          &ldquo;I need more scouts&rdquo;).
        </li>
        <li>
          Every time you talk with the Beekeeper the game restarts with new code for each bee type.
        </li>
      </ul>
    ),
  },
  {
    glyph: '🗂️',
    title: 'Each bee type has its own little program.',
    body: (
      <ul style={LIST_STYLE}>
        <li>
          You start with five bee types <BeeChipRow />.
        </li>
        <li>
          Tap a bee type <BeeChip silent /> to see its code (only if you&rsquo;re curious - you can
          skip the code entirely!).
        </li>
        <li>
          Just ask the <BeekeeperMark />
          <b>Beekeeper</b> in the <b>prompt bar</b> about the code of your bees - it will read the
          code and tell you what it does.
        </li>
      </ul>
    ),
  },
  MAP_STEP,
  OPTIMISE_STEP,
];

export const FIRST_RUN_GUIDE_STEP_COUNT = LIVE_GUIDE_STEPS.length;

export interface FirstRunGuideProps {
  /** The current step index (store-held `guideStep`), clamped into range here. */
  step: number;
  /** Advance to the next step (shown on every step but the last). */
  onNext: () => void;
  /** Return to the previous step (shown on every step but the first). */
  onBack: () => void;
  /** Dismiss the guide ("Got it" on the last step, "Skip" any time) — the caller
   * persists the acknowledgement and flips the durable mirror. */
  onDismiss: () => void;
  /** The topic sequence to draw. Defaults to {@link LIVE_GUIDE_STEPS}, so `Colony.tsx`
   * passes nothing and stays unchanged; `SandboxScreen.tsx` passes
   * {@link ROUNDS_GUIDE_STEPS}. */
  steps?: readonly GuideStep[];
}

const SCRIM_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  // Above the Colony sheet scrim, the create-type flow (zIndex 8), and the field
  // underlay — a fixed top layer for the whole screen (design D-Guide).
  zIndex: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  // The overlay scrim (sketch `.scrim`) — a component-local literal (`--ink-1` at
  // 55%). Deliberately NOT a shared kit token: the Stage-4 lane arbiter
  // (docs/dev/plans/stage4/lanes.md) puts kit-wide token changes out of scope for all
  // four concurrent lanes, so this scrim stays scoped to the onboarding component
  // rather than added to `prototype/tokens/colors.css` (implementation-review F3).
  background: 'rgba(43, 33, 20, 0.55)',
};

const CARD_STYLE: CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: 320,
  background: 'var(--surface-card)',
  border: '1.5px solid var(--line-strong)',
  borderRadius: 'var(--r-lg)',
  boxShadow: 'var(--shadow-sheet)',
  padding: 'var(--sp-5)',
};

const SKIP_STYLE: CSSProperties = {
  position: 'absolute',
  top: 8,
  right: 10,
  minHeight: 44,
  padding: '0 10px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  color: 'var(--text-muted)',
};

const GLYPH_STYLE: CSSProperties = {
  width: 52,
  height: 52,
  borderRadius: 'var(--r-md)',
  background: 'var(--accent-soft)',
  border: '1.5px solid var(--honey-300)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 26,
  marginBottom: 'var(--sp-3)',
};

const KICKER_STYLE: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 'var(--tracking-caps)',
  fontSize: 11,
  color: 'var(--accent-strong)',
  marginBottom: 6,
};

const TITLE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 'var(--text-xl)',
  lineHeight: 1.15,
  margin: '0 0 8px',
  color: 'var(--ink-1)',
};

const COPY_STYLE: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  lineHeight: 1.5,
  color: 'var(--ink-2)',
  margin: 0,
};

const DOTS_STYLE: CSSProperties = { display: 'flex', gap: 6, marginTop: 'var(--sp-5)' };

const ACTIONS_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  marginTop: 'var(--sp-3)',
};

const BTN_BASE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 15,
  minHeight: 44,
  padding: '0 22px',
  borderRadius: 'var(--r-pill)',
  cursor: 'pointer',
};

const PRIMARY_BTN: CSSProperties = {
  ...BTN_BASE,
  background: 'var(--accent-fill)',
  color: '#FFFDF6',
  border: '1px solid var(--accent-strong)',
};

const GHOST_BTN: CSSProperties = {
  ...BTN_BASE,
  background: 'transparent',
  color: 'var(--ink-2)',
  border: '1px solid var(--line-strong)',
};

function dotStyle(active: boolean): CSSProperties {
  return {
    width: active ? 20 : 8,
    height: 8,
    borderRadius: 999,
    background: active ? 'var(--accent)' : 'var(--line-strong)',
  };
}

export function FirstRunGuide({
  step,
  onNext,
  onBack,
  onDismiss,
  steps = LIVE_GUIDE_STEPS,
}: FirstRunGuideProps) {
  const total = steps.length;
  const clamped = Math.max(0, Math.min(step, total - 1));
  const current = steps[clamped]!;
  const isFirst = clamped === 0;
  const isLast = clamped === total - 1;

  return (
    <div
      data-testid="first-run-guide"
      role="dialog"
      aria-modal="true"
      aria-label="First-run guide"
      style={SCRIM_STYLE}
    >
      <div style={CARD_STYLE}>
        <button type="button" aria-label="Skip the guide" onClick={onDismiss} style={SKIP_STYLE}>
          Skip
        </button>
        <div style={GLYPH_STYLE} aria-hidden="true">
          {current.glyph}
        </div>
        <div data-testid="first-run-guide-step" style={KICKER_STYLE}>
          Field notes · {clamped + 1} of {total}
        </div>
        <h2 style={TITLE_STYLE}>{current.title}</h2>
        {/* A `div`, not a `p`: the bodies are bullet lists now (WAG-109) and a `ul` inside a `p`
            is invalid HTML that React will not nest as written. */}
        <div style={COPY_STYLE}>{current.body}</div>
        <div style={DOTS_STYLE} aria-hidden="true">
          {steps.map((_, i) => (
            <span key={i} style={dotStyle(i === clamped)} />
          ))}
        </div>
        <div style={ACTIONS_STYLE}>
          {isFirst ? (
            <span />
          ) : (
            <button type="button" onClick={onBack} style={GHOST_BTN}>
              Back
            </button>
          )}
          {isLast ? (
            <button type="button" onClick={onDismiss} style={PRIMARY_BTN}>
              Got it
            </button>
          ) : (
            <button type="button" onClick={onNext} style={PRIMARY_BTN}>
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
